import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelXS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { smallTeamPictureUrl } from '@sorare/core/src/lib/so5';

import type { SingleMatchupItem_anyGame } from './__generated__/index.graphql';

const Item = styled(Horizontal).attrs({ gap: 0.5, center: true })`
  width: 100%;
  height: 100%;
  color: var(--c-nd-600);

  &.highlighted {
    background-color: var(--c-nd-50);
    color: var(--c-white);
  }

  &:not(:last-child) {
    border-right: 1px solid var(--c-black);
  }
`;
const Img = styled.img`
  object-fit: contain;
`;

type Props = {
  playerTeamSlug?: string;
  anyGame: SingleMatchupItem_anyGame;
};

export const SingleMatchupItem = ({ playerTeamSlug, anyGame }: Props) => {
  const { formatMessage } = useIntl();

  const isUsSport = anyGame.sport !== Sport.FOOTBALL;
  const isPlayingHome = playerTeamSlug === anyGame.homeTeam?.slug;

  const homeTeamPictureUrl = anyGame.homeTeam
    ? smallTeamPictureUrl(anyGame.homeTeam)
    : null;
  const awayTeamPictureUrl = anyGame.awayTeam
    ? smallTeamPictureUrl(anyGame.awayTeam)
    : null;

  const singleMatchup = [
    <Item
      key={1}
      className={classNames({ highlighted: isPlayingHome })}
      aria-label={formatMessage(fantasy.team)}
    >
      {!!homeTeamPictureUrl && (
        <Img
          src={homeTeamPictureUrl}
          alt={anyGame.homeTeam?.slug}
          width={12}
          height={12}
        />
      )}
      <LabelXS>{anyGame.homeTeam?.code}</LabelXS>
    </Item>,
    <Item
      key={2}
      className={classNames({ highlighted: !isPlayingHome })}
      aria-label={formatMessage(fantasy.team)}
    >
      <LabelXS>{anyGame.awayTeam?.code}</LabelXS>
      {!!awayTeamPictureUrl && (
        <Img
          src={awayTeamPictureUrl}
          alt={anyGame.awayTeam?.slug}
          width={12}
          height={12}
        />
      )}
    </Item>,
  ];
  return isUsSport ? singleMatchup.reverse() : singleMatchup;
};

SingleMatchupItem.fragments = {
  anyGame: gql`
    fragment SingleMatchupItem_anyGame on AnyGameInterface {
      id
      sport
      homeTeam {
        slug
        code
        ...smallTeamPictureUrl_teamInterface
      }
      awayTeam {
        slug
        code
        ...smallTeamPictureUrl_teamInterface
      }
    }
    ${smallTeamPictureUrl.fragments.teamInterface}
  ` as TypedDocumentNode<SingleMatchupItem_anyGame>,
};
